<script setup lang="ts">
  // POS V2 points redemption for a viewed order. Opened via
  // showDynamicDialog<IClubRedeemResult | null>(..., 'club-redeem').
  // Redemption needs the member identified: either a live member code (the
  // server consumes it) or the cashier's still-valid identification grant
  // (`presetMember` + `presetVerified`, from the order's club context), in
  // which case the code step is skipped. Unlike the attach modal there is
  // no name/phone lookup fallback.
  import axios from 'axios';
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  import {
    ClubService,
    isClubCodeRequiredError,
  } from '@/api/services/club.service';
  import {
    maxRedeemablePoints,
    redeemPresetPoints,
    valueForPoints,
  } from '@/domain/club/club-program-math';
  import {
    CLUB_CODE_HINT_KEYS,
    TClubCodeHint,
  } from '@/domain/club/club-verification';
  import { DiscountType } from '@/enums/DiscountType';
  import { IClubMember, IClubOrderMember } from '@/interfaces/IClubMember';
  import { IOrder } from '@/interfaces/IOrder';
  import { roundPrice } from '@/utils';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';

  const props = withDefaults(
    defineProps<{
      order: IOrder;
      /** Member attached to the order (POS V2 hosts). */
      presetMember?: IClubOrderMember | null;
      /** The cashier's identification grant is live: skip the code step. */
      presetVerified?: boolean;
    }>(),
    { presetMember: null, presetVerified: false }
  );

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const money = usePosMoney();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const restaurantId = computed(() => restaurantStore.restaurant?.id ?? null);
  const program = computed(() => restaurantStore.restaurant?.club ?? null);

  // One key per modal open: retrying a failed request replays instead of
  // double-applying (the server matches it against existing club discounts).
  const idempotencyKey = uuidv4();

  // Only id/name/memberNumber/pointsBalance are read from either shape.
  const selected = ref<IClubMember | IClubOrderMember | null>(
    props.presetVerified ? props.presetMember : null
  );
  // Identified through the grant: the redeem is sent without a code.
  const useGrant = ref(!!props.presetMember && props.presetVerified);
  // Why the code step shows for a preset member: no live grant covers this
  // order ('needed'), or the server just rejected the grant with 428
  // ('expired'). Null for the plain code-first flow.
  const codeHint = ref<TClubCodeHint | null>(
    props.presetMember && !props.presetVerified ? 'needed' : null
  );
  const codeHintText = computed(() =>
    codeHint.value ? i18nT(CLUB_CODE_HINT_KEYS[codeHint.value]) : null
  );

  //#region Code entry (auto-resolve at 6 digits)

  const codeInput = ref('');
  const resolving = ref(false);
  const codeError = ref<string | null>(null);

  // The resolved code is kept: the redeem call consumes it server-side.
  let resolvedCode = '';

  // Guards against a stale response landing after the input changed.
  let resolveSeq = 0;

  const codeWrapRef = ref<HTMLElement>();

  function focusCodeInput() {
    codeWrapRef.value?.querySelector('input')?.focus();
  }

  onMounted(focusCodeInput);

  function onCodeInput(value: string) {
    codeError.value = null;
    codeInput.value = value.replace(/\D+/g, '').slice(0, 6);
    if (codeInput.value.length === 6) void resolveCode();
  }

  async function resolveCode() {
    if (codeInput.value.length !== 6 || resolving.value) return;
    if (!restaurantId.value) return;

    const seq = ++resolveSeq;
    resolving.value = true;
    try {
      const member = await ClubService.identify(
        restaurantId.value,
        codeInput.value
      );
      if (seq !== resolveSeq) return;
      resolvedCode = codeInput.value;
      useGrant.value = false;
      codeHint.value = null;
      selected.value = member;
    } catch (e) {
      if (seq !== resolveSeq) return;
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        codeError.value = i18nT('posV2.clubInvalidCode');
        codeInput.value = '';
        focusCodeInput();
      } else {
        ui.serverError(e, i18nT('posV2.clubResolveError'));
      }
    } finally {
      if (seq === resolveSeq) resolving.value = false;
    }
  }

  //#endregion

  //#region Redeem form

  // Value already discounted through prior club redemptions on this order:
  // the percent cap applies to the pre-club total, so it must be added back.
  const existingClubValue = computed(() =>
    (props.order.discounts ?? [])
      .filter((discount) => discount.type === DiscountType.Club)
      .reduce((acc, discount) => acc + +discount.value, 0)
  );

  const maxPoints = computed(() => {
    if (!program.value || !selected.value) return 0;
    return maxRedeemablePoints({
      program: program.value,
      orderTotal: +props.order.total,
      roundDifference: +(props.order.roundDifference ?? 0),
      deliveryCost: +(props.order.deliveryCost ?? 0),
      totalPaid: +(props.order.totalPaid ?? 0),
      existingClubValue: existingClubValue.value,
      memberPoints: selected.value.pointsBalance,
    });
  });

  const minPoints = computed(() => program.value?.minRedeemPoints ?? 0);

  /** Why the member can't redeem at all, or null when the form is usable. */
  const blockedReason = computed<string | null>(() => {
    if (!selected.value) return null;
    if (selected.value.pointsBalance <= 0)
      return i18nT('posV2.clubRedeemNoPoints');
    if (maxPoints.value <= 0) return i18nT('posV2.clubRedeemNoAmount');
    if (maxPoints.value < minPoints.value)
      return i18nT('posV2.clubRedeemBelowMin', { points: minPoints.value });
    return null;
  });

  const pointsInput = ref('');
  // Amount chosen before a 428 fallback: restored once the code is typed.
  let pendingPoints: number | null = null;

  // Prefill with the maximum: the common gesture is "use my points".
  watch(
    selected,
    (member) => {
      if (!member || maxPoints.value <= 0) {
        pointsInput.value = '';
        return;
      }
      const restore =
        pendingPoints != null && pendingPoints <= maxPoints.value
          ? pendingPoints
          : maxPoints.value;
      pendingPoints = null;
      pointsInput.value = String(restore);
    },
    { immediate: true }
  );

  function onPointsInput(value: string) {
    pointsInput.value = value.replace(/\D+/g, '');
  }

  const points = computed(() => {
    const num = Number(pointsInput.value);
    return Number.isInteger(num) && num > 0 ? num : 0;
  });

  interface IRedeemPreset {
    points: number;
    label: string;
    value: string;
  }

  /** Quick picks; the last one is always the max ("Todo"). */
  const presets = computed<IRedeemPreset[]>(() => {
    const config = program.value;
    if (!config || blockedReason.value) return [];
    const steps = redeemPresetPoints({
      maxPoints: maxPoints.value,
      minPoints: minPoints.value,
      redeemPointsCost: config.redeemPointsCost,
    });
    return steps.map((step, index) => ({
      points: step,
      label:
        index === steps.length - 1
          ? i18nT('posV2.clubRedeemAll', { points: step })
          : String(step),
      value: money.formatComputedMoney(valueForPoints(config, step)),
    }));
  });

  function selectPreset(presetPoints: number) {
    pointsInput.value = String(presetPoints);
  }

  const previewValue = computed(() =>
    program.value ? valueForPoints(program.value, points.value) : 0
  );

  // order.total is already net of existing discounts: only this redeem moves it.
  const previewNewTotal = computed(() =>
    roundPrice(Math.max(+props.order.total - previewValue.value, 0))
  );

  const pointsError = computed<string | null>(() => {
    if (!pointsInput.value || blockedReason.value) return null;
    if (points.value <= 0) return null;
    if (points.value > maxPoints.value)
      return i18nT('posV2.clubRedeemMaxError', { points: maxPoints.value });
    if (minPoints.value > 0 && points.value < minPoints.value)
      return i18nT('posV2.clubRedeemMinError', { points: minPoints.value });
    return null;
  });

  const canConfirm = computed(
    () =>
      !blockedReason.value &&
      points.value > 0 &&
      points.value <= maxPoints.value &&
      (minPoints.value <= 0 || points.value >= minPoints.value)
  );

  //#endregion

  const saving = ref(false);

  async function confirm() {
    if (!canConfirm.value || saving.value) return;

    saving.value = true;
    try {
      const result = await ClubService.redeem({
        orderId: props.order.id,
        points: points.value,
        code: useGrant.value ? undefined : resolvedCode,
        idempotencyKey,
      });
      modals.dialogSubmit('club-redeem', result);
    } catch (e) {
      saving.value = false;
      if (isClubCodeRequiredError(e)) {
        // The grant no longer covers this order: ask for the code, keeping
        // the chosen amount for after identification. No error toast.
        pendingPoints = points.value;
        backToCode();
        codeHint.value = 'expired';
        return;
      }
      ui.serverError(e, i18nT('posV2.clubRedeemError'));
      // The server consumes the code before applying the ledger entry, so a
      // failed code-based redeem may have burnt it: go back to code entry
      // instead of retrying a code that is likely dead. The grant path sent
      // no code, so the member step stays for a retry.
      if (!useGrant.value) backToCode();
    }
  }

  function backToCode() {
    selected.value = null;
    useGrant.value = false;
    codeHint.value = null;
    codeInput.value = '';
    resolvedCode = '';
    codeError.value = null;
    void Promise.resolve().then(focusCodeInput);
  }

  function cancel() {
    modals.dialogSubmit('club-redeem', null);
  }
</script>

<template>
  <div class="v2-modal-club-redeem">
    <div class="modal-header">
      <h2 class="modal-title">
        <PosUiIcon name="star-circle" class="title-icon" />
        {{ $t('posV2.clubRedeemTitle') }}
      </h2>
      <!-- The subtitle asks for the code: irrelevant once a member is set,
           and already said by the code hint. -->
      <span v-if="!selected && !codeHintText" class="modal-subtitle">
        {{ $t('posV2.clubRedeemSubtitle') }}
      </span>
    </div>

    <!-- Redeem form -->
    <div v-if="selected" class="member-card">
      <div class="member-identity">
        <span class="member-name">{{ selected.name }}</span>
        <span class="member-number">
          {{ $t('posV2.clubMemberNumber', { number: selected.memberNumber }) }}
          · {{ selected.pointsBalance }} {{ $t('posV2.clubPointsSuffix') }}
        </span>
      </div>

      <p v-if="blockedReason" class="blocked-reason">
        <PosUiIcon name="alert-circle-outline" class="blocked-icon" />
        {{ blockedReason }}
      </p>

      <template v-else>
        <div class="redeem-form">
          <span class="form-label">{{ $t('posV2.clubRedeemPointsLabel') }}</span>
          <div v-if="presets.length" class="redeem-presets">
            <button
              v-for="preset in presets"
              :key="preset.points"
              type="button"
              class="preset-button"
              :class="{ 'is-selected': points === preset.points }"
              :disabled="saving"
              @click="selectPreset(preset.points)"
            >
              <span class="preset-points">{{ preset.label }}</span>
              <span class="preset-value">{{ preset.value }}</span>
            </button>
          </div>
          <PosUiInput
            :value="pointsInput"
            :disabled="saving"
            size="lg"
            inputmode="numeric"
            class="points-input"
            @input="onPointsInput"
            @enter="confirm"
          />
          <div class="redeem-caption">
            <span class="caption-max">
              {{ $t('posV2.clubRedeemMax', { points: maxPoints }) }}
              <template v-if="minPoints > 0">
                · {{ $t('posV2.clubRedeemMin', { points: minPoints }) }}
              </template>
            </span>
          </div>
          <p v-if="pointsError" class="points-error">{{ pointsError }}</p>
        </div>

        <div class="redeem-preview" :class="{ empty: !canConfirm }">
          <div class="preview-row">
            <span class="preview-label">{{ $t('posV2.discount') }}</span>
            <span class="preview-value">
              -{{ money.formatComputedMoney(previewValue) }}
            </span>
          </div>
          <span v-if="canConfirm" class="preview-total">
            {{
              $t('posV2.clubRedeemNewTotal', {
                value: money.formatComputedMoney(previewNewTotal),
              })
            }}
          </span>
        </div>
      </template>

      <div class="member-actions">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          class="btn-back"
          :disabled="saving"
          @click="backToCode"
        >
          {{ $t('posV2.clubOtherCode') }}
        </PosUiButton>
        <PosUiButton
          v-if="!blockedReason"
          color="primary"
          variant="solid"
          size="lg"
          class="btn-redeem"
          :disabled="!canConfirm || saving"
          :loading="saving"
          @click="confirm"
        >
          {{
            canConfirm
              ? $t('posV2.clubRedeemApply', {
                  value: money.formatComputedMoney(previewValue),
                })
              : $t('posV2.clubRedeemConfirm')
          }}
        </PosUiButton>
      </div>
    </div>

    <!-- Code entry -->
    <div v-else class="code-section">
      <!-- Preset member without a live grant: name who to ask, then why. -->
      <div v-if="presetMember && codeHintText" class="code-member">
        <span class="code-member-name">{{ presetMember.name }}</span>
        <span class="code-member-number">
          {{ $t('posV2.clubMemberNumber', { number: presetMember.memberNumber }) }}
        </span>
      </div>
      <p v-if="codeHintText" class="code-expired-hint">
        <PosUiIcon
          :name="codeHint === 'expired' ? 'timer-off-outline' : 'shield-account-outline'"
          class="hint-icon"
        />
        {{ codeHintText }}
      </p>
      <div ref="codeWrapRef" class="code-input-wrap">
        <PosUiInput
          :value="codeInput"
          :placeholder="$t('posV2.clubCodePlaceholder')"
          :disabled="resolving"
          size="lg"
          icon="qrcode-scan"
          inputmode="numeric"
          :maxlength="6"
          class="code-input"
          @input="onCodeInput"
          @enter="resolveCode"
        />
      </div>

      <div v-if="resolving" class="code-status">
        <PosUiSkeleton class="status-skeleton" />
      </div>
      <p v-else-if="codeError" class="code-error">
        <PosUiIcon name="alert-circle-outline" class="error-icon" />
        {{ codeError }}
      </p>
      <p v-else-if="!codeHintText" class="code-hint">
        {{ $t('posV2.clubCodeHint') }}
      </p>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-cancel"
        :disabled="saving"
        @click="cancel"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-club-redeem {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-text-lg tw-font-bold tw-text-neutral-900;

        .title-icon {
          @apply tw-text-xl tw-text-orange-600;
        }
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .member-card {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-4;

      .member-identity {
        @apply tw-flex tw-flex-col tw-gap-0.5;

        .member-name {
          @apply tw-text-lg tw-font-bold tw-text-neutral-900;
        }

        .member-number {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }
      }

      .blocked-reason {
        @apply tw-mb-0 tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2.5 tw-text-sm tw-font-medium tw-text-amber-800;

        .blocked-icon {
          @apply tw-flex-shrink-0 tw-text-base;
        }
      }

      .redeem-form {
        @apply tw-flex tw-flex-col tw-gap-1;

        .form-label {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }

        .redeem-presets {
          @apply tw-mb-1 tw-grid tw-grid-cols-4 tw-gap-1.5;

          .preset-button {
            @apply tw-flex tw-h-12 tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-1 tw-leading-tight tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

            &.is-selected {
              // Inset ring doubles the border without moving the content.
              @apply tw-border-orange-600 tw-bg-orange-50 tw-text-orange-900 tw-ring-1 tw-ring-inset tw-ring-orange-600;
            }

            &:disabled {
              @apply tw-cursor-not-allowed tw-opacity-60;
            }

            .preset-points {
              @apply tw-w-full tw-truncate tw-text-center tw-text-sm tw-font-bold tw-tabular-nums;
            }

            .preset-value {
              @apply tw-w-full tw-truncate tw-text-center tw-text-[11px] tw-tabular-nums tw-text-neutral-500;
            }
          }
        }

        .redeem-caption {
          @apply tw-text-xs tw-text-neutral-500;
        }

        .points-error {
          @apply tw-mb-0 tw-text-xs tw-font-medium tw-text-red-600;
        }
      }

      .redeem-preview {
        @apply tw-flex tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-py-2.5;

        &.empty {
          @apply tw-border-neutral-200 tw-bg-white;

          .preview-value {
            @apply tw-text-neutral-400;
          }
        }

        .preview-row {
          @apply tw-flex tw-items-center tw-justify-between;
        }

        .preview-label {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }

        .preview-value {
          @apply tw-text-base tw-font-bold tw-tabular-nums tw-text-emerald-700;
        }

        .preview-total {
          @apply tw-text-right tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-600;
        }
      }

      .member-actions {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-2;

        :deep(.btn-back),
        :deep(.btn-redeem) {
          @apply tw-min-w-[130px];
        }
      }
    }

    .code-section {
      @apply tw-flex tw-flex-col tw-gap-3;

      .code-member {
        @apply tw-flex tw-items-baseline tw-gap-1.5;

        .code-member-name {
          @apply tw-text-base tw-font-bold tw-text-neutral-900;
        }

        .code-member-number {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }
      }

      .code-expired-hint {
        @apply tw-mb-0 tw-flex tw-items-start tw-gap-1.5 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2.5 tw-text-sm tw-font-medium tw-text-amber-800;

        .hint-icon {
          @apply tw-mt-0.5 tw-flex-shrink-0 tw-text-base;
        }
      }
    }

    .code-input-wrap {
      @apply tw-w-full;

      :deep(.code-input .pos-ui-input-field) {
        @apply tw-text-center tw-text-xl tw-font-bold tw-tracking-[0.4em];
      }
    }

    .code-status {
      @apply tw-flex tw-justify-center;

      .status-skeleton {
        @apply tw-h-5 tw-w-40;
      }
    }

    .code-error {
      @apply tw-mb-0 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-sm tw-font-medium tw-text-red-600;

      .error-icon {
        @apply tw-text-base;
      }
    }

    .code-hint {
      @apply tw-mb-0 tw-text-center tw-text-sm tw-text-neutral-500;
    }

    .modal-footer {
      @apply tw-flex tw-justify-end;
    }
  }
</style>
